import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { ChangeLogMonitorService } from '../../../services/change-log-monitor.service';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  buildHeaderDataShell,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-retrigger-message',
    templateUrl: './retrigger-message.component.html',
    styleUrls: ['./retrigger-message.component.scss', '../../../slt-dashboard.styles.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RetriggerMessageComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // STANDARD COMPONENT PROPERTIES
  modalTitle = "Retrigger Message";
  minimise = false;
  isSaveClicked = false;

  // Component-specific properties
  saveloader = false;
  tags: string[] = [];
  leKey: string[] = [];
  leType: string[] = [];
  submissionId: string[] = [];
  filingGroupKey: string[] = [];

  // Data properties
  baseURLs: any = {};
  headerData: any = {};
  rowData: any = {};
  filterParams: any = {};
  gridData: any[] = [];
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  globalService: any = {};

  private readonly subs = new Subscription();

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private changeLogMonitorService: ChangeLogMonitorService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.headerData = header;
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.globalService = header;
    const g = gridPayloadFromActiveScreen(activeScreen);
    this.gridData = Array.isArray(g) ? g : g != null ? [g] : [];
    if (this.gridData.length) {
      this.processGridData();
    }
    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  init() {
    try {
      // Initial setup if needed
    } catch (e) {
      console.error('Error in RetriggerMessage init:', e);
    }
  }

  processGridData() {
    // Process grid data to extract IDs and related data
    this.tags = [];
    this.leKey = [];
    this.leType = [];
    this.submissionId = [];
    this.filingGroupKey = [];

    this.gridData.forEach((item: any) => {
      const data = item.data || item;
      this.tags.push(data.LEID);
      this.leKey.push(data.LE_KEY);
      this.leType.push(data.LE_TYPE);
      this.submissionId.push(data.SUBMISSION_ID);
      this.filingGroupKey.push(data.FILING_GROUP_KEY);
    });

    this.cdr.detectChanges();
  }

  removeTag(index: number) {
    // Remove element from all related arrays
    this.tags.splice(index, 1);
    this.leKey.splice(index, 1);
    this.leType.splice(index, 1);
    this.submissionId.splice(index, 1);
    this.filingGroupKey.splice(index, 1);
    this.cdr.detectChanges();
  }

  reTriggerMessage() {
    this.isSaveClicked = true;
    this.saveloader = true;
    this.cdr.detectChanges();

    const params = {
      client_key: this.USER_SETTINGS.user?.client_key,
      le_key: this.leKey.toString(),
      le_type: this.leType.toString(),
      submissionId: this.submissionId.toString()
    };

    this.changeLogMonitorService.saveRetriggerMessage(
      this.GENERAL_CONFIG.bulk_pdf_engine_api_url,
      params
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.saveloader = false;

        if (response.callSuccess == "1") {
          this._snackBar.open('Message Retriggered Successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        } else {
          const errorMessage = response.data?.message || response.message || 'Error occurred while retriggering message';
          this._snackBar.open(errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }

        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error retriggering message:', error);
        this.isSaveClicked = false;
        this.saveloader = false;
        this._snackBar.open('Error occurred while retriggering message. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
